import { useNavigate } from "react-router-dom";
import background from "../assets/background.png";
import AppBarSettings from "../components/AppBarSettings";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useState, useEffect } from "react";
import {
  generateOverlayUrl,
  generateNotificationUrl,
  copyToClipboard,
  parseUrlParams,
  OverlayParams,
  NotificationParams,
} from "../utils/urlParams";
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "../api";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import notificationService from "../services/notificationService";
import logoAnimationService from "../services/logoAnimationService";
import { LogoAnimationSettings } from "../types";

const Settings = () => {
  const navigate = useNavigate();
  const [overlayParams, setOverlayParams] = useState<OverlayParams>({
    width: "800px",
    height: "600px",
    refresh_frequency: "30",
    show_count: "5",
    show_count_extended: "10",
    theme: "default",
    opacity: "1.0",
    animation_enabled: "false",
    animation_frequency: "60",
    animation_duration: "10",
  });
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [generatedNotificationUrl, setGeneratedNotificationUrl] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [notificationCopySuccess, setNotificationCopySuccess] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: true,
    interval: 30,
    animationEnabled: true,
    soundEnabled: false,
    theme: 'dark' as 'light' | 'dark',
  });
  const [logoAnimationSettings, setLogoAnimationSettings] = useState<LogoAnimationSettings>({
    enabled: false,
    frequency: 60,
    duration: 10,
    textTemplate: "{{sender}} donated {{amount}} coins for {{challenge}}",
  });

  const handleLogOut = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    navigate("/");
  };

  const navigateToOpps = () => {
    navigate("/opps");
  };

  // Load current access token and overlay settings from localStorage
  useEffect(() => {
    const loadSettings = async () => {
      const urlParams = await parseUrlParams();

      if (urlParams.access_token) {
        localStorage.setItem(ACCESS_TOKEN_KEY, urlParams.access_token);
        const url = new URL(window.location.href);
        url.searchParams.delete("access_token");
        window.history.replaceState({}, "", url.pathname + url.search);
      }

      const accessToken =
        urlParams.access_token || localStorage.getItem(ACCESS_TOKEN_KEY);

      // Get current state from localStorage (set by Opps component)
      const currentState =
        localStorage.getItem("current_opps_state") || "tagged";

      // Load logo animation settings
      const logoAnimSettings = logoAnimationService.getSettings();
      setLogoAnimationSettings(logoAnimSettings);

      const currentParams: OverlayParams = {
        access_token: accessToken || undefined,
        width: localStorage.getItem("overlay_width") || "800px",
        height: localStorage.getItem("overlay_height") || "600px",
        refresh_frequency:
          localStorage.getItem("overlay_refresh_frequency") || "30",
        show_count: localStorage.getItem("overlay_show_count") || "5",
        show_count_extended:
          localStorage.getItem("overlay_show_count_extended") || "10",
        theme: localStorage.getItem("overlay_theme") || "default",
        opacity: localStorage.getItem("overlay_opacity") || "1.0",
        state: currentState,
        animation_enabled: logoAnimSettings.enabled.toString(),
        animation_frequency: logoAnimSettings.frequency.toString(),
        animation_duration: logoAnimSettings.duration.toString(),
      };

      setOverlayParams(currentParams);

      // Load notification settings
      const notificationSettings = notificationService.getSettings();
      setNotificationSettings(notificationSettings);

      if (accessToken) {
        const url = await generateOverlayUrl(
          currentParams,
          window.location.origin + "/opps"
        );
        setGeneratedUrl(url);
      }

      // Generate notification URL
      const notificationParams: NotificationParams = {
        notifications_enabled: notificationSettings.enabled.toString(),
        notifications_interval: notificationSettings.interval.toString(),
        notifications_animation:
          notificationSettings.animationEnabled.toString(),
        notifications_sound: notificationSettings.soundEnabled.toString(),
        notifications_theme: notificationSettings.theme,
      };
      const notificationUrl = await generateNotificationUrl(
        notificationParams,
        window.location.origin + "/notifications"
      );
      setGeneratedNotificationUrl(notificationUrl);
    };

    loadSettings();
  }, []);

  // Update URL when params change
  useEffect(() => {
    const updateUrl = async () => {
      if (overlayParams.access_token) {
        // Ensure current state is included in the URL generation
        const currentState =
          overlayParams.state === "created" || overlayParams.state === "tagged"
            ? overlayParams.state
            : localStorage.getItem("current_opps_state") || "tagged";
        const paramsWithState = { ...overlayParams, state: currentState };

        const url = await generateOverlayUrl(
          paramsWithState,
          window.location.origin + "/opps"
        );
        setGeneratedUrl(url);
      }
    };

    updateUrl();
  }, [overlayParams]);

  // Allow free typing; normalize size on blur
  const handleParamChange = (key: keyof OverlayParams, value: string) => {
    if (key === "width" || key === "height") {
      // Do not normalize yet; let the user type numbers freely
      setOverlayParams((prev) => ({ ...prev, [key]: value }));
      return;
    }

    // Non-size params update immediately
    setOverlayParams((prev) => ({ ...prev, [key]: value }));
    if (key === "refresh_frequency") {
      localStorage.setItem("overlay_refresh_frequency", value);
    } else if (key === "show_count") {
      localStorage.setItem("overlay_show_count", value);
    } else if (key === "show_count_extended") {
      localStorage.setItem("overlay_show_count_extended", value);
    } else if (key === "theme") {
      localStorage.setItem("overlay_theme", value);
    } else if (key === "opacity") {
      localStorage.setItem("overlay_opacity", value);
    } else if (key === "state") {
      localStorage.setItem("current_opps_state", value);
    } else if (key === "animation_enabled") {
      const enabled = value === "true";
      logoAnimationService.updateSetting("enabled", enabled);
      setLogoAnimationSettings(prev => ({ ...prev, enabled }));
    } else if (key === "animation_frequency") {
      const frequency = parseInt(value, 10);
      if (!isNaN(frequency) && frequency > 0) {
        logoAnimationService.updateSetting("frequency", frequency);
        setLogoAnimationSettings(prev => ({ ...prev, frequency }));
      }
    } else if (key === "animation_duration") {
      const duration = parseInt(value, 10);
      if (!isNaN(duration) && duration > 0) {
        logoAnimationService.updateSetting("duration", duration);
        setLogoAnimationSettings(prev => ({ ...prev, duration }));
      }
    }

    window.dispatchEvent(new CustomEvent("overlaySettingsChanged"));
  };

  const normalizeAndPersistSize = (
    key: "width" | "height",
    rawValue: string
  ) => {
    const numericValue = parseFloat((rawValue || "").replace(/[^\d.]/g, ""));
    const minValue = key === "width" ? 200 : 100;
    const finalNumber = Math.max(
      isNaN(numericValue) ? minValue : numericValue,
      minValue
    );

    // Detect unit
    const unitMatch = (rawValue || "").match(/(px|vw|vh|%|em|rem)$/);
    const unit = unitMatch ? unitMatch[1] : "px";
    const normalized = `${finalNumber}${unit}`;

    setOverlayParams((prev) => ({ ...prev, [key]: normalized }));

    if (key === "width") {
      localStorage.setItem("overlay_width", normalized);
    } else {
      localStorage.setItem("overlay_height", normalized);
    }

    // Update size locally for immediate preview
    window.dispatchEvent(new CustomEvent("overlaySettingsChanged"));
  };

  const handleCopyUrl = async () => {
    const success = await copyToClipboard(generatedUrl);
    setCopySuccess(success);
    if (success) {
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleCopyNotificationUrl = async () => {
    const success = await copyToClipboard(generatedNotificationUrl);
    setNotificationCopySuccess(success);
    if (success) {
      setTimeout(() => setNotificationCopySuccess(false), 2000);
    }
  };

  const handleNotificationSettingChange = (
    key: keyof typeof notificationSettings,
    value: any
  ) => {
    const newSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(newSettings);
    notificationService.setSettings(newSettings);

    // Update notification URL when settings change
    const updateNotificationUrl = async () => {
      const notificationParams: NotificationParams = {
        notifications_enabled: newSettings.enabled.toString(),
        notifications_interval: newSettings.interval.toString(),
        notifications_animation: newSettings.animationEnabled.toString(),
        notifications_sound: newSettings.soundEnabled.toString(),
        notifications_theme: newSettings.theme,
      };
      const notificationUrl = await generateNotificationUrl(
        notificationParams,
        window.location.origin + "/notifications"
      );
      setGeneratedNotificationUrl(notificationUrl);
    };
    updateNotificationUrl();
  };

  return (
    <div
      style={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "100vh",
        textAlign: "center",
        position: "relative",
      }}
    >
      <AppBarSettings navigateTo={navigateToOpps} handleLogOut={handleLogOut} />

      <Box
        sx={{
          padding: { xs: 2, sm: 3 },
          maxWidth: 800,
          width: "100%",
          margin: "0 auto",
          flex: 1,
          overflow: "auto",
          paddingBottom: 10,
          boxSizing: "border-box",
        }}
      >
        <Typography variant="h4" color="white" marginBottom={3} marginTop={3}>
          Overlay Settings
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" color="white" marginBottom={2}>
              Overlay URL:
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                fullWidth
                value={generatedUrl}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.3)",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "white",
                    fontSize: "0.875rem",
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleCopyUrl}
                startIcon={<ContentCopyIcon />}
                sx={{
                  background:
                    "linear-gradient(217deg, rgba(95, 224, 227, 1), rgba(139, 101, 236, 1))",
                  minWidth: "auto",
                  px: 2,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4)",
                    background:
                      "linear-gradient(217deg, rgba(95, 224, 227, 1), rgba(139, 101, 236, 1)), linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%)",
                    backgroundSize: "100% 100%, 200% 100%",
                    animation: "shine 0.6s ease-in-out",
                  },
                  transition: "all 0.3s ease",
                  "@keyframes shine": {
                    "0%": {
                      backgroundPosition: "0% 0%, -200% 0",
                    },
                    "100%": {
                      backgroundPosition: "0% 0%, 200% 0",
                    },
                  },
                }}
              >
                Copy
              </Button>
              <Button
                variant="contained"
                onClick={() => window.open(generatedUrl, "_blank")}
                sx={{
                  background:
                    "linear-gradient(217deg, rgba(95, 224, 227, 1), rgba(139, 101, 236, 1))",
                  minWidth: "auto",
                  px: 2,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4)",
                    background:
                      "linear-gradient(217deg, rgba(95, 224, 227, 1), rgba(139, 101, 236, 1)), linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%)",
                    backgroundSize: "100% 100%, 200% 100%",
                    animation: "shine 0.6s ease-in-out",
                  },
                  transition: "all 0.3s ease",
                  "@keyframes shine": {
                    "0%": {
                      backgroundPosition: "0% 0%, -200% 0",
                    },
                    "100%": {
                      backgroundPosition: "0% 0%, 200% 0",
                    },
                  },
                }}
              >
                Preview
              </Button>
            </Box>
          </Grid>
          {/* Notification URL Section */}
          <Grid item xs={12} marginBottom={3}>
            <Typography
              variant="h6"
              color="white"
              marginBottom={2}
              marginTop={1}
            >
              Alerts URL:
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                fullWidth
                value={generatedNotificationUrl}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.3)",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "white",
                    fontSize: "0.875rem",
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleCopyNotificationUrl}
                startIcon={<ContentCopyIcon />}
                sx={{
                  background:
                    "linear-gradient(217deg, rgba(95, 224, 227, 1), rgba(139, 101, 236, 1))",
                  minWidth: "auto",
                  px: 2,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4)",
                    background:
                      "linear-gradient(217deg, rgba(95, 224, 227, 1), rgba(139, 101, 236, 1)), linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%)",
                    backgroundSize: "100% 100%, 200% 100%",
                    animation: "shine 0.6s ease-in-out",
                  },
                  transition: "all 0.3s ease",
                  "@keyframes shine": {
                    "0%": {
                      backgroundPosition: "0% 0%, -200% 0",
                    },
                    "100%": {
                      backgroundPosition: "0% 0%, 200% 0",
                    },
                  },
                }}
              >
                Copy
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/notification-preview")}
                sx={{
                  background:
                    "linear-gradient(217deg, rgba(95, 224, 227, 1), rgba(139, 101, 236, 1))",
                  minWidth: "auto",
                  px: 2,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4)",
                    background:
                      "linear-gradient(217deg, rgba(95, 224, 227, 1), rgba(139, 101, 236, 1)), linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%)",
                    backgroundSize: "100% 100%, 200% 100%",
                    animation: "shine 0.6s ease-in-out",
                  },
                  transition: "all 0.3s ease",
                  "@keyframes shine": {
                    "0%": {
                      backgroundPosition: "0% 0%, -200% 0",
                    },
                    "100%": {
                      backgroundPosition: "0% 0%, 200% 0",
                    },
                  },
                }}
              >
                Preview
              </Button>
            </Box>
            <Typography
              variant="body2"
              color="rgba(255, 255, 255, 0.7)"
              marginTop={1}
            >
              This URL opens a transparent overlay for notifications only. Use
              this for OBS or streaming software.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Width"
              value={overlayParams.width}
              onChange={(e) => handleParamChange("width", e.target.value)}
              onBlur={(e) => normalizeAndPersistSize("width", e.target.value)}
              placeholder="e.g., 800px, 100vw"
              helperText="Minimum: 200px (px, vw, vh, %, em, rem supported)"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                },
                "& .MuiOutlinedInput-input": {
                  color: "white",
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Height"
              value={overlayParams.height}
              onChange={(e) => handleParamChange("height", e.target.value)}
              onBlur={(e) => normalizeAndPersistSize("height", e.target.value)}
              placeholder="e.g., 600px, 100vh"
              helperText="Minimum: 100px (px, vw, vh, %, em, rem supported)"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                },
                "& .MuiOutlinedInput-input": {
                  color: "white",
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Refresh Frequency (seconds)"
              value={overlayParams.refresh_frequency}
              onChange={(e) =>
                handleParamChange("refresh_frequency", e.target.value)
              }
              placeholder="30"
              type="number"
              helperText="Recommended: 30 seconds or more"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                },
                "& .MuiOutlinedInput-input": {
                  color: "white",
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Show Count (Short)"
              value={overlayParams.show_count}
              onChange={(e) => handleParamChange("show_count", e.target.value)}
              placeholder="5"
              type="number"
              helperText="Number of opportunities in short list"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                },
                "& .MuiOutlinedInput-input": {
                  color: "white",
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Show Count (Extended)"
              value={overlayParams.show_count_extended}
              onChange={(e) =>
                handleParamChange("show_count_extended", e.target.value)
              }
              placeholder="10"
              type="number"
              helperText="Number of opportunities in extended list"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                },
                "& .MuiOutlinedInput-input": {
                  color: "white",
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: "white" }}>Opacity</InputLabel>
              <Select
                value={overlayParams.opacity}
                onChange={(e) => handleParamChange("opacity", e.target.value)}
                label="Opacity"
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.7)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "rgba(32, 46, 64, 0.95)",
                      color: "white",
                      "& .MuiMenuItem-root": {
                        color: "white",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "rgba(95, 224, 227, 0.3)",
                        },
                      },
                    },
                  },
                }}
              >
                {Array.from({ length: 20 }, (_, i) => {
                  const value = ((i + 1) * 0.05).toFixed(2);
                  const percentage = Math.round(parseFloat(value) * 100);
                  return (
                    <MenuItem key={value} value={value}>
                      {value} ({percentage}%)
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                mt: 1,
                display: "block",
              }}
            >
              Overlay transparency (0.05 = 5% minimum, 1.00 = opaque)
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: "white" }}>Opportunities</InputLabel>
              <Select
                value={overlayParams.state === "created" ? "created" : "tagged"}
                onChange={(e) => handleParamChange("state", e.target.value)}
                label="Opportunities"
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.7)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "rgba(32, 46, 64, 0.95)",
                      color: "white",
                      "& .MuiMenuItem-root": {
                        color: "white",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "rgba(95, 224, 227, 0.3)",
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="tagged">Tagged</MenuItem>
                <MenuItem value="created">Created</MenuItem>
              </Select>
            </FormControl>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                mt: 1,
                display: "block",
              }}
            >
              Which list the overlay opens on
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" color="white" marginBottom={2}>
              For settings to take effect, copy the newly generated URL
            </Typography>
          </Grid>

          {/* Logo Animation Settings Section */}
          <Grid item xs={12}>
            <Typography
              variant="h5"
              color="white"
              marginBottom={3}
              marginTop={4}
            >
              Logo Animation Settings
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={logoAnimationSettings.enabled}
                  onChange={(e) =>
                    handleParamChange("animation_enabled", e.target.checked.toString())
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "rgba(95, 224, 227, 1)",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "rgba(95, 224, 227, 0.5)",
                    },
                  }}
                />
              }
              label={
                <Typography color="white" sx={{ fontFamily: "Outfit" }}>
                  Enable Logo Animation
                </Typography>
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Animation Frequency (seconds)"
              value={logoAnimationSettings.frequency}
              onChange={(e) =>
                handleParamChange("animation_frequency", e.target.value)
              }
              placeholder="60"
              type="number"
              disabled={!logoAnimationSettings.enabled}
              helperText="How often the logo animation appears (minimum: 10 seconds)"
              inputProps={{ min: 10 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                },
                "& .MuiOutlinedInput-input": {
                  color: "white",
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Animation Duration (seconds)"
              value={logoAnimationSettings.duration}
              onChange={(e) =>
                handleParamChange("animation_duration", e.target.value)
              }
              placeholder="10"
              type="number"
              disabled={!logoAnimationSettings.enabled}
              helperText="How long the logo animation lasts (minimum: 1 second)"
              inputProps={{ min: 1 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                },
                "& .MuiOutlinedInput-input": {
                  color: "white",
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body2"
              color="rgba(255, 255, 255, 0.7)"
              marginTop={2}
            >
              When enabled, the overlay will normally shrink to a small size (120x120px) showing only the logo in the upper left corner. At the specified intervals, it will expand to full size and show the complete opportunities list. At the halfway point of the animation duration, it will automatically switch between "created" and "tagged" states, then return to the small logo state.
            </Typography>
          </Grid>

          {/* Notification Settings Section */}
          <Grid item xs={12}>
            <Typography
              variant="h5"
              color="white"
              marginBottom={3}
              marginTop={4}
            >
              Notification Settings
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.enabled}
                  onChange={(e) =>
                    handleNotificationSettingChange("enabled", e.target.checked)
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "rgba(95, 224, 227, 1)",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "rgba(95, 224, 227, 0.5)",
                    },
                  }}
                />
              }
              label={
                <Typography color="white" sx={{ fontFamily: "Outfit" }}>
                  Enable Notifications
                </Typography>
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.animationEnabled}
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "animationEnabled",
                      e.target.checked
                    )
                  }
                  disabled={!notificationSettings.enabled}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "rgba(95, 224, 227, 1)",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "rgba(95, 224, 227, 0.5)",
                    },
                  }}
                />
              }
              label={
                <Typography color="white" sx={{ fontFamily: "Outfit" }}>
                  Enable Animations
                </Typography>
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Notification Check Interval (seconds)"
              value={notificationSettings.interval}
              onChange={(e) =>
                handleNotificationSettingChange(
                  "interval",
                  parseInt(e.target.value) || 30
                )
              }
              placeholder="30"
              type="number"
              disabled={!notificationSettings.enabled}
              helperText="How often to check for new notifications (minimum: 5 seconds)"
              inputProps={{ min: 5 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "white",
                },
                "& .MuiOutlinedInput-input": {
                  color: "white",
                },
                "& .MuiFormHelperText-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.soundEnabled}
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "soundEnabled",
                      e.target.checked
                    )
                  }
                  disabled={!notificationSettings.enabled}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "rgba(95, 224, 227, 1)",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "rgba(95, 224, 227, 0.5)",
                    },
                  }}
                />
              }
              label={
                <Typography color="white" sx={{ fontFamily: "Outfit" }}>
                  Enable Sound (Future Feature)
                </Typography>
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: "white" }}>Animation Theme</InputLabel>
              <Select
                value={notificationSettings.theme}
                onChange={(e) => handleNotificationSettingChange("theme", e.target.value)}
                label="Animation Theme"
                disabled={!notificationSettings.enabled}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.7)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "rgba(32, 46, 64, 0.95)",
                      color: "white",
                      "& .MuiMenuItem-root": {
                        color: "white",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "rgba(95, 224, 227, 0.3)",
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="light">Light</MenuItem>
                <MenuItem value="dark">Dark</MenuItem>
              </Select>
            </FormControl>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                mt: 1,
                display: "block",
              }}
            >
              Choose light or dark themed animations for challenges and donations
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body2"
              color="rgba(255, 255, 255, 0.7)"
              marginTop={2}
            >
              Notifications will check for new challenges and donations. When
              enabled, new notifications will trigger full-screen animations.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        style={{ position: "fixed", bottom: 20 }}
      >
        <Alert onClose={() => setCopySuccess(false)} severity="success">
          URL copied to clipboard!
        </Alert>
      </Snackbar>

      <Snackbar
        open={notificationCopySuccess}
        autoHideDuration={2000}
        onClose={() => setNotificationCopySuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        style={{ position: "fixed", bottom: 20 }}
      >
        <Alert
          onClose={() => setNotificationCopySuccess(false)}
          severity="success"
        >
          Notification URL copied to clipboard!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Settings;
